import { forwardRef, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { HistogramChart, CollapsibleCard, DonutChart } from '@mesh.ai/rabbit';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { checkCollapsibleCardsEnabled, checkGraphAccessEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';

import ChartContainer from '../../ChartContainer/ChartContainer';

const PerformanceChart = forwardRef<
  typeof HistogramChart,
  {
    chartData: [];
    keyResultsName: string;
    pluralKeyResultsName: string;
  }
>(({ chartData, pluralKeyResultsName, keyResultsName }, ref) => (
  <HistogramChart
    tooltipTitle={`${keyResultsName} Progress Distribution`}
    xAxisLabel="Progress Percentage"
    yAxisLabel={`Count of ${pluralKeyResultsName}`}
    data={chartData}
    integerBins
    integerYAxis
    // @ts-expect-error ref mismatch
    ref={ref}
  />
));

interface Props {
  showGraphs: boolean;
  chartData: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rowData: Array<any>;
  };
  donutChartData: {
    label: string;
    labelColor: string;
    value: number;
    color: string;
  }[];
}

const GroupByGoalGraphs = ({ showGraphs, chartData, donutChartData }: Props) => {
  const { moduleName, keyResultsName } = useOrganisationSettings();
  const history = useHistory();
  const dispatch = useDispatch();
  const histogramChartRef = useRef();
  const donutChartRef = useRef();
  const { l1Module } = useRouteToNavLevels();
  const currentURL = history.location.pathname;

  const localStorageKeyForChart = getUniqueCardKey({ url: currentURL, cardId: 'myGoalsChart' });

  const isChartOpen = useLocalStorageCards({
    localStorageKey: localStorageKeyForChart,
    card: 'myGoalsChart',
    section: 'myData',
  });

  const handleChartChange = (newVal) => {
    dispatch(
      setIsCardOpen({
        card: 'myGoalsChart',
        section: 'myData',
        isCardOpen: newVal,
        localStorageKey: localStorageKeyForChart,
      })
    );
  };

  // CollapsibleCard feature is behind a feature flag
  //  we will remove this once we integrate visier charts to my team pages and make collapsible cards consistent across all modules landing pages.
  const isNewNavEnabled = checkNewNavEnabled();
  const isCollapsibleCardsEnabled = checkCollapsibleCardsEnabled();
  const isGraphAccessEnabled = checkGraphAccessEnabled();

  return (
    !isMobileView() &&
    isGraphAccessEnabled &&
    showGraphs &&
    (isCollapsibleCardsEnabled ? (
      <CollapsibleCard
        disabled={!isNewNavEnabled}
        onOpenChange={handleChartChange}
        open={isChartOpen}
        title="Distribution"
      >
        <Flex w="100%" borderRadius="8px" padding="8px" gap={2} background="#F5F7F9">
          {isNewNavEnabled && l1Module === 'myData' && (
            <Box w="50%">
              <ChartContainer
                chartData={chartData}
                chartRef={donutChartRef}
                chartTitle={`${modifyString(moduleName, StringModifier.TITLE_CASE)} status`}
              >
                <Box
                  className="rabbit-ds"
                  style={{
                    height: '300px',
                    width: '100%',
                  }}
                >
                  <DonutChart ref={donutChartRef} data={donutChartData} />
                </Box>
              </ChartContainer>
            </Box>
          )}
          <Box w="50%">
            <ChartContainer
              chartData={chartData}
              chartRef={histogramChartRef}
              chartTitle={`${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)} progress distribution`}
            >
              <PerformanceChart
                //   @ts-expect-error data type yet to be defined
                chartData={
                  chartData?.rowData
                    ?.filter((data) => data.type === OKR_TYPES.KEY_RESULT)
                    ?.map((data) => data?.avgProgressPercentage || 0) || []
                }
                keyResultsName={modifyString(keyResultsName, StringModifier.TITLE_CASE)}
                pluralKeyResultsName={modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}
              />
            </ChartContainer>
          </Box>
        </Flex>
      </CollapsibleCard>
    ) : (
      <ChartContainer
        chartData={chartData}
        chartRef={histogramChartRef}
        chartTitle={`${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)} progress distribution`}
      >
        <PerformanceChart
          //   @ts-expect-error data type yet to be defined
          chartData={
            chartData?.rowData
              ?.filter((data) => data.type === OKR_TYPES.KEY_RESULT)
              ?.map((data) => data?.avgProgressPercentage || 0) || []
          }
          keyResultsName={modifyString(keyResultsName, StringModifier.TITLE_CASE)}
          pluralKeyResultsName={modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}
        />
      </ChartContainer>
    ))
  );
};

export { GroupByGoalGraphs };
